(function () {
  'use strict';

  var DEFAULT_BASE = '#0D39FF';
  var DEFAULT_ACCENT = '#2950FF';
  var UNDERLAY_COLOR = '#000000';
  var ANIM_SPEED = 4;

  function getColors(el) {
    var style = getComputedStyle(el || document.documentElement);
    return {
      base: style.getPropertyValue('--color-dotted-button-base').trim() || DEFAULT_BASE,
      accent: style.getPropertyValue('--color-dotted-button-hover').trim() || DEFAULT_ACCENT,
    };
  }

  function createHoverSound(url) {
    if (!url) {
      return function () {};
    }

    var audio = new Audio(url);
    audio.volume = 0.2;
    audio.preload = 'auto';

    return function () {
      var clone = audio.cloneNode();
      clone.volume = 0.2;
      clone.play().catch(function () {});
    };
  }

  function DotsGridCanvas(canvas) {
    this.canvas = canvas;
    this.colorRoot = canvas.closest('.dot-grid') || document.documentElement;
    this.ctx = null;
    this.blockHover = false;
    this.time = 0;
    this.animate = false;
    this.animateDirection = 1;
    this.glitchState = [];
    this.dotVulnerability = [];
    this.columnPositions = [];
    this.rowPositions = [];
    this.rect = { width: 0, height: 0 };
    this.gridSpecs = {
      dotSpacing: 1,
      cols: 0,
      rows: 0,
      marginX: 0,
      marginY: 0,
      dotSize: 1,
    };
    this.rafId = null;
    this.lastFrameTime = null;
    this.colors = getColors(this.colorRoot);

    this.onResize = this.onResize.bind(this);
    this.tick = this.tick.bind(this);
  }

  DotsGridCanvas.prototype.computeGridSpecs = function (width, height) {
    if (!width || !height) {
      return {
        dotSpacing: 1,
        cols: 0,
        rows: 0,
        marginX: 0,
        marginY: 0,
        dotSize: 1,
      };
    }

    var desiredRows = 14;
    var alpha = 1;
    var calculatedDotSize = height / (desiredRows * (1 + alpha) + 1);
    var calculatedDotSpacing = alpha * calculatedDotSize;
    var dotSize = calculatedDotSize;
    var dotSpacing = calculatedDotSpacing;
    var dotArea = dotSpacing + dotSize;
    var cols = Math.floor((width - dotSize) / dotArea);
    var rows = desiredRows;
    var totalGridWidth = cols * dotSize + (cols > 0 ? (cols - 1) * dotSpacing : 0);
    var marginX = (width - totalGridWidth) / 2;
    var totalGridHeight = rows * dotSize + (rows > 0 ? (rows - 1) * dotSpacing : 0);
    var marginY = (height - totalGridHeight) / 2;

    return {
      dotSpacing: dotSpacing,
      cols: cols,
      rows: rows,
      marginX: marginX,
      marginY: marginY,
      dotSize: dotSize,
    };
  };

  DotsGridCanvas.prototype.initGridState = function () {
    var specs = this.gridSpecs;
    var glitchState = [];
    var vulnerabilities = [];

    for (var i = 0; i < specs.rows; i++) {
      var rowGlitch = [];
      var rowVuln = [];
      for (var j = 0; j < specs.cols; j++) {
        rowGlitch.push(false);
        rowVuln.push(Math.random());
      }
      glitchState.push(rowGlitch);
      vulnerabilities.push(rowVuln);
    }

    this.glitchState = glitchState;
    this.dotVulnerability = vulnerabilities;
  };

  DotsGridCanvas.prototype.updatePositions = function () {
    var specs = this.gridSpecs;
    var colsPositions = [];
    var rowsPositions = [];

    for (var j = 0; j < specs.cols; j++) {
      colsPositions.push(specs.marginX + j * (specs.dotSize + specs.dotSpacing));
    }
    for (var i = 0; i < specs.rows; i++) {
      rowsPositions.push(specs.marginY + i * (specs.dotSize + specs.dotSpacing));
    }

    this.columnPositions = colsPositions;
    this.rowPositions = rowsPositions;
  };

  DotsGridCanvas.prototype.updateDotState = function (i, j, progress) {
    if (!this.dotVulnerability[i] || this.glitchState[i] === undefined) {
      return null;
    }

    var isGlitched = this.glitchState[i][j];
    var vulnerability = this.dotVulnerability[i][j];

    if (this.animateDirection === 1) {
      if (!isGlitched && progress >= vulnerability) {
        this.glitchState[i][j] = true;
        isGlitched = true;
      }
    } else if (isGlitched && progress < vulnerability) {
      this.glitchState[i][j] = false;
      isGlitched = false;
    }

    return this.blockHover ? true : isGlitched;
  };

  DotsGridCanvas.prototype.drawGrid = function (progress) {
    var specs = this.gridSpecs;
    var ctx = this.ctx;
    var width = this.rect.width;
    var height = this.rect.height;

    if (!ctx || !width || !height || specs.cols === 0 || specs.rows === 0) {
      return;
    }

    ctx.clearRect(0, 0, width, height);

    // Black underlay — same cell origins as dots (matches web2 DotsGridCanvas)
    ctx.fillStyle = UNDERLAY_COLOR;
    for (var ui = 0; ui < specs.rows; ui++) {
      var uy = this.rowPositions[ui];
      var cellH = ui < specs.rows - 1 ? specs.dotSize + specs.dotSpacing : specs.dotSize;

      for (var uj = 0; uj < specs.cols; uj++) {
        var ux = this.columnPositions[uj];
        var cellW = uj < specs.cols - 1 ? specs.dotSize + specs.dotSpacing : specs.dotSize;
        ctx.fillRect(ux, uy, cellW, cellH);
      }
    }

    var lastFillStyle = null;

    for (var i = 0; i < specs.rows; i++) {
      var dotTopLeftY = this.rowPositions[i];

      for (var j = 0; j < specs.cols; j++) {
        var dotState = this.updateDotState(i, j, progress);
        if (dotState === null) {
          continue;
        }

        var dotTopLeftX = this.columnPositions[j];
        var colorToUse = dotState ? this.colors.accent : this.colors.base;

        if (lastFillStyle !== colorToUse) {
          ctx.fillStyle = colorToUse;
          lastFillStyle = colorToUse;
        }

        ctx.fillRect(dotTopLeftX, dotTopLeftY, specs.dotSize, specs.dotSize);
      }
    }
  };

  DotsGridCanvas.prototype.setupCanvas = function () {
    var wrap = this.canvas.parentElement;
    if (!wrap) {
      return;
    }

    var width = wrap.clientWidth;
    var height = wrap.clientHeight;
    this.rect = { width: width, height: height };
    this.gridSpecs = this.computeGridSpecs(width, height);

    if (this.gridSpecs.cols === 0 || this.gridSpecs.rows === 0) {
      return;
    }

    this.initGridState();
    this.updatePositions();

    var ctx = this.canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    this.ctx = ctx;
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, width, height);

    var dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    this.drawGrid(this.blockHover ? 1 : -1);
  };

  DotsGridCanvas.prototype.startAnimation = function () {
    if (this.rafId !== null) {
      return;
    }

    this.lastFrameTime = null;
    var self = this;
    this.rafId = window.requestAnimationFrame(function (time) {
      self.rafId = null;
      self.tick(time);
    });
  };

  DotsGridCanvas.prototype.tick = function (time) {
    if (!this.animate) {
      return;
    }

    if (this.lastFrameTime === null) {
      this.lastFrameTime = time;
    }

    var deltaTime = time - this.lastFrameTime;
    this.lastFrameTime = time;

    if (this.time > 1) {
      this.time = 1;
      this.animate = false;
      this.drawGrid(this.time);
      return;
    }

    if (this.time < 0) {
      this.time = 0;
      this.animate = false;
      this.drawGrid(this.time);
      return;
    }

    this.time += deltaTime * 0.0016 * ANIM_SPEED * this.animateDirection;
    this.drawGrid(this.time);

    if (this.animate) {
      var self = this;
      this.rafId = window.requestAnimationFrame(function (nextTime) {
        self.rafId = null;
        self.tick(nextTime);
      });
    }
  };

  DotsGridCanvas.prototype.onResize = function () {
    this.colors = getColors(this.colorRoot);
    this.setupCanvas();
  };

  DotsGridCanvas.prototype.mount = function () {
    this.setupCanvas();

    if (typeof ResizeObserver !== 'undefined') {
      var wrap = this.canvas.parentElement;
      if (wrap) {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(wrap);
      }
    } else {
      window.addEventListener('resize', this.onResize);
    }
  };

  DotsGridCanvas.prototype.destroy = function () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    } else {
      window.removeEventListener('resize', this.onResize);
    }

    if (this.rafId !== null) {
      window.cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  };

  DotsGridCanvas.prototype.active = function () {
    this.blockHover = true;
  };

  DotsGridCanvas.prototype.inactive = function () {
    this.blockHover = false;
  };

  DotsGridCanvas.prototype.enter = function () {
    if (this.blockHover) {
      return;
    }

    this.time = 0;
    this.animate = true;
    this.animateDirection = 1;
    this.startAnimation();
  };

  DotsGridCanvas.prototype.exit = function () {
    if (this.blockHover) {
      return;
    }

    this.time = 1;
    this.animate = true;
    this.animateDirection = -1;
    this.startAnimation();
  };

  function initLink(link, playHover) {
    var canvas = link.querySelector('.dot-grid__canvas');
    if (!canvas || link.dataset.dotGridInit === '1') {
      return;
    }

    link.dataset.dotGridInit = '1';

    var grid = new DotsGridCanvas(canvas);
    grid.mount();

    link.addEventListener('mouseenter', function () {
      if (link.disabled) {
        return;
      }
      playHover();
      grid.enter();
    });

    link.addEventListener('mouseleave', function () {
      grid.exit();
    });

    link.addEventListener('focus', function () {
      if (link.disabled) {
        return;
      }
      playHover();
      grid.enter();
    });

    link.addEventListener('blur', function () {
      grid.exit();
    });

    if (link.classList.contains('is-active')) {
      window.requestAnimationFrame(function () {
        grid.enter();
        grid.active();
      });
    }
  }

  function init(root) {
    var config = window.webpunkDottedNav || {};
    var playHover = createHoverSound(config.hoverSoundUrl);
    var scope = root || document;
    var links = scope.querySelectorAll('.dot-grid');

    for (var i = 0; i < links.length; i++) {
      initLink(links[i], playHover);
    }
  }

  window.webpunkDottedNavInit = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }
})();
